// services/cookie.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CookieService {
  private readonly COOKIE_NAME = 'cookie-preferences';
  private preferencesSubject = new BehaviorSubject<CookiePreferences>(this.getPreferences());
  
  public preferences$: Observable<CookiePreferences> = this.preferencesSubject.asObservable();

  constructor() {
    // Verificar se já existe consentimento
    if (!this.hasConsent()) {
      this.showBanner();
    }
  }

  private getPreferences(): CookiePreferences {
    const cookie = this.getCookie(this.COOKIE_NAME);
    if (cookie) {
      return JSON.parse(cookie);
    }
    
    // Valores padrão
    return {
      necessary: true, // Sempre true, não pode ser desativado
      analytics: false,
      marketing: false,
      preferences: false
    };
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    
    return null;
  }

  private setCookie(name: string, value: string, days: number): void {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    
    document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
  }

  public savePreferences(preferences: CookiePreferences): void {
    // Garantir que cookies necessários sempre sejam true
    preferences.necessary = true;
    
    const preferencesString = JSON.stringify(preferences);
    this.setCookie(this.COOKIE_NAME, preferencesString, 365); // Expira em 1 ano
    
    this.preferencesSubject.next(preferences);
    this.hideBanner();
    
    // Aplicar as preferências
    this.applyPreferences(preferences);
  }

  public hasConsent(): boolean {
    return !!this.getCookie(this.COOKIE_NAME);
  }

  public getCurrentPreferences(): CookiePreferences {
    return this.preferencesSubject.value;
  }

  private showBanner(): void {
    // Será controlado pelo componente
  }

  private hideBanner(): void {
    // Será controlado pelo componente
  }

  private applyPreferences(preferences: CookiePreferences): void {
    // Aplicar ou remover scripts baseado nas preferências
    this.handleAnalyticsCookies(preferences.analytics);
    this.handleMarketingCookies(preferences.marketing);
    this.handlePreferenceCookies(preferences.preferences);
  }

  private handleAnalyticsCookies(enabled: boolean): void {
    if (enabled) {
      this.loadAnalyticsScripts();
    } else {
      this.removeAnalyticsScripts();
    }
  }

  private handleMarketingCookies(enabled: boolean): void {
    if (enabled) {
      this.loadMarketingScripts();
    } else {
      this.removeMarketingScripts();
    }
  }

  private handlePreferenceCookies(enabled: boolean): void {
    if (enabled) {
      // Carregar scripts de preferências
    } else {
      // Remover scripts de preferências
    }
  }

  private loadAnalyticsScripts(): void {
    // Exemplo: Google Analytics
    const script = document.createElement('script');
    script.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'GA_MEASUREMENT_ID');
    `;
    document.head.appendChild(script);
  }

  private removeAnalyticsScripts(): void {
    // Remover scripts de analytics
    const scripts = document.querySelectorAll('script[src*="google-analytics"]');
    scripts.forEach(script => script.remove());
  }

  private loadMarketingScripts(): void {
    // Carregar scripts de marketing (Facebook Pixel, etc.)
  }

  private removeMarketingScripts(): void {
    // Remover scripts de marketing
  }
}